import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import RefreshButton from "../../components/admin/RefreshButton";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useAdminToast } from "../../context/AdminToastContext";

// Partners (airlines / tourism boards / hotel groups) are ADMIN-PROVISIONED —
// there is no self-signup. Admin creates the login + org here, grants à-la-carte
// feature access; each partner is then managed from its profile page.

const PARTNER_TYPES = [
  "airline", "tourism_board", "hotel_group", "hotel_chain", "cruise_line",
  "association", "convention_bureau", "airport", "theme_park", "attraction",
  "event_organizer", "other",
];

const PARTNER_FEATURES = [
  "showcase", "announcements", "news",
  "featured", "analytics",
];
const FEATURE_LABEL: Record<string, string> = {
  showcase: "Showcase", announcements: "Announcements", news: "News",
  webinars: "Webinars", campaigns: "Campaigns", featured: "Featured",
  analytics: "Analytics",
};

const typeLabel = (t: string) => t.replace(/_/g, " ");

export interface PartnerRow {
  id: string;
  owner_user_id: string;
  organization_name: string;
  partner_type: string;
  is_verified: boolean;
  status: string;
  onboarding_done: boolean;
  features: Record<string, boolean>;
  brand_logo?: string;
}

const EMPTY_FORM = {
  login_email: "", initial_password: "", contact_name: "", phone: "",
  organization_name: "", partner_type: "airline", headquarters_country: "",
  website: "", official_email: "",
};

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const labelCls = "block text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-1.5";
const inputCls =
  "w-full px-3 py-2 bg-gray-900 border border-gray-700/80 rounded-md text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors";

function FeatureToggle({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
        on ? "bg-indigo-500/10 border-indigo-500/40 text-indigo-300"
           : "bg-transparent border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-300"
      }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-indigo-400" : "bg-gray-600"}`} />
      {label}
    </button>
  );
}

// ── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl px-5 py-4">
      <div className={`text-2xl font-semibold tabular-nums ${tint}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export default function AdminPartnersPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [query, setQuery] = useState("");

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [grant, setGrant] = useState<Record<string, boolean>>({ showcase: true });
  const [creating, setCreating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const flash = (msg: string, ok = true) => {
    if (ok) { setSuccess(msg); setError(""); } else { setError(msg); setSuccess(""); }
    setTimeout(() => { setSuccess(""); setError(""); }, 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<PartnerRow[]>("/api/admin/partners", {
        params: { limit: 200 }, headers: adminHeaders(),
      });
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(parseApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const stats = useMemo(() => ({
    total: partners.length,
    active: partners.filter((p) => p.status === "active").length,
    unverified: partners.filter((p) => !p.is_verified).length,
    onboarded: partners.filter((p) => p.onboarding_done).length,
  }), [partners]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter((p) =>
      p.organization_name.toLowerCase().includes(q) ||
      p.partner_type.toLowerCase().includes(q) ||
      p.owner_user_id.toLowerCase().includes(q));
  }, [partners, query]);

  const createPartner = async () => {
    if (!form.login_email || form.initial_password.length < 8 || !form.contact_name || !form.organization_name) {
      flash("Login email, an 8+ char password, contact name and organisation are required.", false);
      return;
    }
    setCreating(true);
    try {
      const features = PARTNER_FEATURES.filter((f) => grant[f])
        .map((f) => ({ feature: f, enabled: true, quota_monthly: -1 }));
      await apiClient.post("/api/admin/partners", { ...form, features }, { headers: adminHeaders() });
      flash(`Partner "${form.organization_name}" provisioned.`);
      setForm({ ...EMPTY_FORM }); setGrant({ showcase: true }); setAddOpen(false);
      await load();
    } catch (e) {
      flash(parseApiError(e).message, false);
    } finally {
      setCreating(false);
    }
  };

  const openProfile = (p: PartnerRow) =>
    navigate(`/admin/partners/${p.owner_user_id}`, { state: { partner: p } });

  const deletePartner = async (p: PartnerRow) => {
    if (!confirm(`Delete "${p.organization_name}"?\n\nThis removes the partner org and its login. Cannot be undone.`)) return;
    setDeletingId(p.id);
    try {
      await apiClient.delete(`/api/admin/partners/${p.owner_user_id}`, { headers: adminHeaders() });
      flash(`Partner "${p.organization_name}" deleted.`);
      setPartners((prev) => prev.filter((row) => row.id !== p.id));
    } catch (e) {
      flash(parseApiError(e).message, false);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-6xl">
      {/* Toasts */}
      {(error || success) && (
        <div className="fixed top-6 right-6 z-50 max-w-sm">
          {error && <div className="px-4 py-3 bg-red-950/90 border border-red-800/60 rounded-lg text-red-200 text-sm shadow-xl backdrop-blur">{error}</div>}
          {success && <div className="px-4 py-3 bg-emerald-950/90 border border-emerald-800/60 rounded-lg text-emerald-200 text-sm shadow-xl backdrop-blur">{success}</div>}
        </div>
      )}

      <AdminPageHeader
        title="Partners"
        subtitle="Airlines, tourism boards & hotel groups — admin-provisioned."
        className="mb-5"
        actions={
          <>
            <RefreshButton onRefresh={load} />
            <button onClick={() => setAddOpen((v) => !v)}
              className="px-3.5 py-2 bg-white text-gray-900 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={addOpen ? "M6 18L18 6M6 6l12 12" : "M12 5v14m7-7H5"} />
              </svg>
              {addOpen ? "Close" : "New partner"}
            </button>
          </>
        }
      />

      {/* Stat cards */}
      {!addOpen && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total partners" value={stats.total} tint="text-white" />
        <StatCard label="Active" value={stats.active} tint="text-indigo-400" />
        <StatCard label="Unverified" value={stats.unverified} tint="text-amber-400" />
        <StatCard label="Onboarded" value={stats.onboarded} tint="text-emerald-400" />
      </div>
      )}

      {/* Add partner (collapsible) */}
      {addOpen && (
        <div className="bg-gray-900/60 rounded-xl border border-gray-800 p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <div><label className={labelCls}>Login email *</label>
              <input className={inputCls} type="email" value={form.login_email}
                onChange={(e) => setForm({ ...form, login_email: e.target.value })} placeholder="ops@airline.com" /></div>
            <div><label className={labelCls}>Initial password * (min 8)</label>
              <input className={inputCls} value={form.initial_password}
                onChange={(e) => setForm({ ...form, initial_password: e.target.value })} placeholder="reset on first login" /></div>
            <div><label className={labelCls}>Contact name *</label>
              <input className={inputCls} value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><label className={labelCls}>Phone</label>
              <input className={inputCls} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className={labelCls}>Organisation name *</label>
              <input className={inputCls} value={form.organization_name}
                onChange={(e) => setForm({ ...form, organization_name: e.target.value })} placeholder="Uzbekistan Airways" /></div>
            <div><label className={labelCls}>Partner type *</label>
              <select className={inputCls} value={form.partner_type}
                onChange={(e) => setForm({ ...form, partner_type: e.target.value })}>
                {PARTNER_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
              </select></div>
            <div><label className={labelCls}>HQ country</label>
              <input className={inputCls} value={form.headquarters_country}
                onChange={(e) => setForm({ ...form, headquarters_country: e.target.value })} /></div>
            <div><label className={labelCls}>Website</label>
              <input className={inputCls} value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
            <div className="md:col-span-2"><label className={labelCls}>Official email</label>
              <input className={inputCls} type="email" value={form.official_email}
                onChange={(e) => setForm({ ...form, official_email: e.target.value })} placeholder="defaults to login email" /></div>
          </div>
          <div className="mt-5">
            <label className={labelCls}>Grant feature access</label>
            <div className="flex flex-wrap gap-2">
              {PARTNER_FEATURES.map((f) => (
                <FeatureToggle key={f} on={!!grant[f]} label={FEATURE_LABEL[f]}
                  onClick={() => setGrant({ ...grant, [f]: !grant[f] })} />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => { setForm({ ...EMPTY_FORM }); setGrant({ showcase: true }); setAddOpen(false); }}
              className="px-3.5 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
            <button onClick={() => void createPartner()} disabled={creating}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-md text-sm font-medium text-white disabled:opacity-50 transition-colors">
              {creating ? "Provisioning…" : "Provision"}
            </button>
          </div>
        </div>
      )}

      {!addOpen && (<>
      {/* Search */}
      <div className="relative mb-3">
        <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, type or owner id…"
          className="w-full max-w-sm pl-9 pr-3 py-2 bg-gray-900 border border-gray-800 rounded-md text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" />
      </div>

      {/* Table */}
      <div className="bg-gray-900/40 border border-gray-800 rounded-xl overflow-hidden">
        {/* Fixed-width columns (Type/Status/Features/Action) can't shrink below
            their pixel widths, so below ~560px they'd collapse the "Partner"
            1fr column to 0 and overlap text. Scroll horizontally instead. */}
        <div className="overflow-x-auto">
        <div className="min-w-[760px]">
        <div className="grid grid-cols-[minmax(0,1fr)_130px_120px_90px_150px] gap-3 px-4 py-2.5 border-b border-gray-800 text-[11px] uppercase tracking-wider text-gray-500 font-medium">
          <div>Partner</div><div>Type</div><div>Status</div>
          <div className="text-right">Features</div><div className="text-right">Action</div>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-800/60">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse bg-gray-900/30" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-gray-400 text-sm">{query ? "No partners match your search." : "No partners yet."}</p>
            {!query && <p className="text-gray-600 text-xs mt-1">Provision one with “New partner”.</p>}
          </div>
        ) : (
          <div className="divide-y divide-gray-800/60">
            {filtered.map((p) => {
              const activeCount = PARTNER_FEATURES.filter((f) => (p.features || {})[f]).length;
              return (
                <div key={p.id}
                  onClick={() => openProfile(p)}
                  className="grid grid-cols-[minmax(0,1fr)_130px_120px_90px_150px] gap-3 px-4 py-3 items-center cursor-pointer hover:bg-gray-800/25 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-md bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 font-semibold text-sm shrink-0 overflow-hidden">
                      {p.brand_logo ? (
                        <img src={p.brand_logo} alt={p.organization_name} className="w-full h-full object-cover" />
                      ) : (
                        (p.organization_name || "?")[0]?.toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate">{p.organization_name}</div>
                      <div className="text-[10px] text-gray-600 font-mono truncate">{p.owner_user_id}</div>
                    </div>
                  </div>
                  <div className="text-gray-400 text-xs capitalize truncate">{typeLabel(p.partner_type)}</div>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      p.is_verified ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
                    }`}>
                      <span className={`w-1 h-1 rounded-full ${p.is_verified ? "bg-emerald-400" : "bg-amber-400"}`} />
                      {p.is_verified ? "Verified" : "Pending"}
                    </span>
                  </div>
                  <div className="text-right text-xs text-gray-400 tabular-nums">
                    {activeCount}<span className="text-gray-600">/{PARTNER_FEATURES.length}</span>
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <button onClick={(e) => { e.stopPropagation(); openProfile(p); }}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md text-xs font-medium text-gray-200 transition-colors">
                      View
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); void deletePartner(p); }}
                      disabled={deletingId === p.id}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-red-950/60 border border-gray-700 hover:border-red-800/60 rounded-md text-xs font-medium text-gray-200 hover:text-red-300 transition-colors disabled:opacity-50">
                      {deletingId === p.id ? "…" : "Delete"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
